import { useState, useEffect } from "react";
import { Clock, AlertTriangle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface CountdownTimerProps {
  targetDate: Date;
  onExpire?: () => void;
  size?: "sm" | "md" | "lg";
  showLabels?: boolean;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
}

const calculateTimeLeft = (targetDate: Date): TimeLeft => {
  const difference = targetDate.getTime() - new Date().getTime();
  
  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
  }

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / 1000 / 60) % 60),
    seconds: Math.floor((difference / 1000) % 60),
    total: difference,
  };
};

export default function CountdownTimer({ 
  targetDate, 
  onExpire, 
  size = "md",
  showLabels = true 
}: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calculateTimeLeft(targetDate));
  const { language } = useLanguage();
  const t = (el: string, en: string) => (language === "el" ? el : en);

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft(targetDate);
      setTimeLeft(newTimeLeft);
      
      if (newTimeLeft.total <= 0) {
        clearInterval(timer);
        onExpire?.();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate, onExpire]);

  if (timeLeft.total <= 0) {
    return (
      <div className="flex items-center gap-2 text-destructive">
        <AlertTriangle className="h-4 w-4" />
        <span className="font-medium">{t("Η προσφορά έληξε", "Offer expired")}</span>
      </div>
    );
  }

  const isUrgent = timeLeft.days === 0 && timeLeft.hours < 12;
  const isCritical = timeLeft.days === 0 && timeLeft.hours < 2;

  const sizeClasses = {
    sm: {
      container: "gap-1",
      box: "w-10 h-10",
      number: "text-sm font-bold",
      label: "text-[9px]",
    },
    md: {
      container: "gap-2",
      box: "w-14 h-14",
      number: "text-lg font-bold",
      label: "text-[10px]",
    },
    lg: {
      container: "gap-3",
      box: "w-20 h-20",
      number: "text-2xl font-bold",
      label: "text-xs",
    },
  };

  const classes = sizeClasses[size];

  const TimeBox = ({ value, label }: { value: number; label: string }) => (
    <div className="flex flex-col items-center">
      <div 
        className={`${classes.box} rounded-lg flex flex-col items-center justify-center transition-all ${
          isCritical 
            ? 'bg-destructive/20 border-2 border-destructive animate-pulse' 
            : isUrgent 
              ? 'bg-warning/20 border-2 border-warning' 
              : 'bg-primary/10 border border-primary/30'
        }`}
      >
        <span className={`${classes.number} ${isCritical ? 'text-destructive' : isUrgent ? 'text-warning' : 'text-primary'}`}>
          {value.toString().padStart(2, '0')}
        </span>
        {showLabels && (
          <span className={`${classes.label} text-muted-foreground uppercase tracking-wider`}>
            {label}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col items-center">
      {(isUrgent || isCritical) && (
        <div className={`flex items-center gap-1 mb-2 ${isCritical ? 'text-destructive' : 'text-warning'}`}>
          <Clock className="h-4 w-4 animate-pulse" />
          <span className="text-xs font-medium">
            {isCritical
              ? t("Τελευταία ευκαιρία!", "Last chance!")
              : t("Η προσφορά λήγει σύντομα!", "Offer ends soon!")}
          </span>
        </div>
      )}
      <div className={`flex items-center ${classes.container}`}>
        {timeLeft.days > 0 && (
          <>
            <TimeBox value={timeLeft.days} label={t("Ημέρες", "Days")} />
            <span className={`${classes.number} ${isCritical ? 'text-destructive' : isUrgent ? 'text-warning' : 'text-primary'} self-start mt-2`}>:</span>
          </>
        )}
        <TimeBox value={timeLeft.hours} label={t("Ώρες", "Hours")} />
        <span className={`${classes.number} ${isCritical ? 'text-destructive' : isUrgent ? 'text-warning' : 'text-primary'} self-start mt-2`}>:</span>
        <TimeBox value={timeLeft.minutes} label={t("Λεπτά", "Mins")} />
        <span className={`${classes.number} ${isCritical ? 'text-destructive' : isUrgent ? 'text-warning' : 'text-primary'} self-start mt-2`}>:</span>
        <TimeBox value={timeLeft.seconds} label={t("Δευτ.", "Sec")} />
      </div>
    </div>
  );
}
